import APIRequester from "src/api/APIRequester"
import LoginHelper from "src/components/login/LoginHelper"
import AuthenticationError from "src/validation/exception_messages/AuthenticationError"
import RedirectToLoginException from "src/validation/exception_messages/RedirectToLoginException"
import FeatureProvider from "src/features/FeatureProvider"
import FeatureFlagIds from "src/features/FeatureFlagIds"
import FeatureFlag from "src/features/FeatureFlag"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import StateConstants from "src/states/StateContstants"


class ApiHandler {
    baseURL: string 
    comparableHomesURL: string 
    valuateURL: string 
    findAddressURL: string 
    suggestAddressURL: string 
    getAddressURL: string 
    getUserInfoURL: string 
    getAddressImageURL: string 
    saveValuationURL: string
    listValuationsURL: string
    deleteValuationURL: string
    createCheckoutURL: string
    findAreaURL: string
    suggestAreaURL: string
    valuationUsageURL: string
    globalUsageURL: string
    apiRequester: APIRequester
    loginHelper: LoginHelper
    stateHandler: EventAdaptingStateHandler
    requestCache: Map<string, any>
    featureProvider: FeatureProvider

    constructor(apiRequester: APIRequester, baseURL: string, loginHelper: LoginHelper, stateHandler: EventAdaptingStateHandler, featureProvider: FeatureProvider) {
        this.apiRequester = apiRequester
        this.baseURL = baseURL
        this.comparableHomesURL = this.baseURL + "/comparable_homes"
        this.valuateURL = this.baseURL + "/valuate"
        this.findAddressURL = this.baseURL + "/find_address"
        this.suggestAddressURL = this.baseURL + "/suggest_address"
        this.getUserInfoURL = this.baseURL + "/get_user_info"
        this.getAddressImageURL = this.baseURL + "/get_address_image"
        this.getAddressURL = this.baseURL + "/get_address"
        this.saveValuationURL = this.baseURL + "/save_valuation"
        this.listValuationsURL = this.baseURL + "/list_valuations"
        this.deleteValuationURL = this.baseURL + "/delete_valuation"
        this.createCheckoutURL = this.baseURL + "/create_checkout"
        this.findAreaURL = this.baseURL + "/find_area"
        this.suggestAreaURL = this.baseURL + "/suggest_area"
        this.valuationUsageURL = this.baseURL + "/valuation_usage"
        this.globalUsageURL = this.baseURL + "/global_usage"
        this.loginHelper = loginHelper;
        this.stateHandler = stateHandler;
        this.requestCache = new Map();
        this.featureProvider = featureProvider;
    }

    clearCache() {
        this.requestCache.clear();
    }

    getCacheKey(url: string, input: any): string {
        return `${url}-${JSON.stringify(input)}`;
    }

    async getAddress(input: any) {
        return await this.postOrLogin(this.getAddressURL, input)
    }

    async getComparableHomes(input: any) {
        return await this.postOrLogin(this.comparableHomesURL, input)
    }

    async getValuation(input: any)  {
        return await this.postOrLogin(this.valuateURL, input)
    }

    async findAddress(input: any) {
        return await this.postOrLogin(this.findAddressURL, input)
    }

    async suggestAddress(input: any) {
        return await this.postOrLogin(this.suggestAddressURL, input)
    }

    async getUserInfo() {
        return await this.postOrLogin(this.getUserInfoURL, {})
    }

    /** Fixed dimensions for card thumbnails so cache keys stay consistent. */
    static readonly THUMBNAIL_WIDTH = 160
    static readonly THUMBNAIL_HEIGHT = 120

    async getAddressImage(input: any) {
        const cacheKey = `img_${input.location_id}_${input.width}_${input.height}`;
        try {
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) return JSON.parse(cached);
        } catch {}

        const response = await this.postOrLogin(this.getAddressImageURL, input);

        if (response?.raw_image && response.valid_image === true) {
            try {
                sessionStorage.setItem(cacheKey, JSON.stringify(response));
            } catch {}
        }

        return response;
    }

    /**
     * Get a cached address thumbnail with fixed size for use in cards.
     * Always uses the same dimensions so backend and client caching stay consistent.
     */
    async getAddressThumbnail(locationId: string, locationString: string) {
        return await this.getAddressImage({
            location_id: locationId,
            location_string: locationString,
            width: ApiHandler.THUMBNAIL_WIDTH,
            height: ApiHandler.THUMBNAIL_HEIGHT,
            return_saved: true,
        })
    }

    async saveValuation(input: any) {
        return await this.postOrLogin(this.saveValuationURL, input)
    }

    async listValuations(input: any, preventForceLogin: boolean = false) {
        return await this.postOrLogin(this.listValuationsURL, input, preventForceLogin)
    }

    async deleteValuation(input: any) {
        return await this.postOrLogin(this.deleteValuationURL, input)
    }

    async createCheckout(email?: string) {
        const input = email ? { email } : {}
        const cacheKey = this.getCacheKey(this.createCheckoutURL, input)
        this.requestCache.delete(cacheKey)
        return await this.postOrLogin(this.createCheckoutURL, input, true)
    }

    async findArea(input: { id?: string; text?: string }) {
        return await this.postOrLogin(this.findAreaURL, input)
    }

    async suggestArea(input: { area: string }) {
        return await this.postOrLogin(this.suggestAreaURL, input)
    }

    async claimPayment(paymentIntentId: string) {
        return await this.postOrLogin(this.baseURL + "/claim_payment", { payment_intent_id: paymentIntentId })
    }

    async getGlobalUsage() {
        return await this.apiRequester.post(this.globalUsageURL, {})
    }

    async getValuationUsage() {
        const cacheKey = this.getCacheKey(this.valuationUsageURL, {})
        this.requestCache.delete(cacheKey)
        return await this.postOrLogin(this.valuationUsageURL, {}, true)
    }

    async postOrLogin(url: string, input: any, preventForceLogin: boolean = false) {
        const cacheKey = this.getCacheKey(url, input);

        if (this.requestCache.has(cacheKey)) {
            return this.requestCache.get(cacheKey);
        }

        let response 
        try {
            let tokenHeader = await this.getTokenHeaderOrLogin()
            response = await this.apiRequester.post(url, input, tokenHeader)
            if (response?.metadata?.li == false) {
                try {
                    await this.loginHelper.logout()
                } catch (error) {
                    // Ignore error
                }
                this.stateHandler.set(StateConstants.IS_LOGGED_IN, false)
            }
            
            this.requestCache.set(cacheKey, response);
            
        } catch(error) {
            if (error instanceof AuthenticationError) {
                if (preventForceLogin) {
                    return null
                }
                await this.loginHelper.logout()
                this.stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
                throw new RedirectToLoginException("User is not authenticated, redirecting to login.")
            } else {
                throw error
            }
        }
        if (response?.metadata?.gb) {
            this.stateHandler.set(StateConstants.IS_GOOGLE_BOT, true)
        }
        return response
    }

    async getTokenHeaderOrLogin() {
        let isAuthRequired: FeatureFlag = this.featureProvider.getFeatureFlag(FeatureFlagIds.REQUIRE_AUTHENTICATION);
        let token: any = await this.loginHelper.getValidToken()
        if (token && Object.keys(token).length > 0) {
            return {
                "x-auth-token": token["id_token"],
                "x-auth-access-token": token["access_token"]
            }
        }
        if (!isAuthRequired.isEnabled()) {
            return {}
        }
        throw new AuthenticationError("User is not logged in, no valid authentication token.")
    }
}

export default ApiHandler
